// useUploadsExtracted - ONE batched fetch of the extracted-facts view for a
// whole set of uploads, plus the context the per-row useUploadExtracted(uploadId)
// hooks read from so they serve off that single batch instead of each firing
// its own /uploads/extracted request. The <UploadsExtractedProvider> that wires
// this into the tree lives in UploadsExtractedProvider.tsx (kept separate so
// this module exports no component - react-refresh / fast-refresh hygiene).
//
// Why this exists: the project Files page mounts useUploadExtracted per file
// row (via ExtractionStatusButton + ExtractionIssueMarker). On a large project
// that fanned out one /uploads/extracted request per row on mount - a ~300-file
// page fired ~300 near-simultaneous requests, each scaling the (uncapped) API
// Lambda into its own execution env + DB connection, exhausting the small RDS
// connection pool ("remaining connection slots are reserved ..."). Principle 31:
// per-item state for a SET of items comes from ONE poll, not N per-item polls.
import { createContext } from "react";
import { useQuery } from "react-query";
import { useAgentsHttp, type AgentsHttp } from "../useAgentsHttp";
import { queryKeys } from "../queryKeys";
import { isExtractionInFlight } from "../../utils/extractionInFlight";
import type { ExtractionView } from "./useUploadExtracted";

// Ids per HTTP request. MUST match _MAX_EXTRACTED_BATCH on the BE; we chunk
// here so a pathologically large project still resolves in ceil(N/500)
// requests (typically one) rather than the BE rejecting the request.
const MAX_BATCH = 500;
const POLL_MS = 4000;

interface BatchResponse {
  views: Record<string, ExtractionView>;
}

// The subset of react-query's result the leaf components read. The provider
// hands one of these to useUploadExtracted so a batched row never fires its
// own request.
export interface UploadExtractedResult {
  data: ExtractionView | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export interface UploadsExtractedContextValue {
  // Whether this provider is responsible for the upload (i.e. the id is in the
  // set it batches). Keyed on the DECLARED id set, not on loaded data, so a row
  // is owned - and its standalone request stays disabled - from the very first
  // render, before the batch resolves (otherwise every row would fire its own
  // request during the initial load window, re-creating the N+1).
  owns: (uploadId: string) => boolean;
  resultFor: (uploadId: string) => UploadExtractedResult;
}

export const UploadsExtractedContext =
  createContext<UploadsExtractedContextValue | null>(null);

const idsSignature = (uploadIds: string[]): string =>
  Array.from(new Set(uploadIds)).sort().join(",");

async function fetchBatch(
  http: AgentsHttp,
  uploadIds: string[],
): Promise<Record<string, ExtractionView>> {
  const unique = Array.from(new Set(uploadIds));
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += MAX_BATCH) {
    chunks.push(unique.slice(i, i + MAX_BATCH));
  }
  const results = await Promise.all(
    chunks.map((chunk) =>
      http<BatchResponse>("/uploads/extracted-batch", { upload_ids: chunk }),
    ),
  );
  const merged: Record<string, ExtractionView> = {};
  for (const r of results) Object.assign(merged, r.views);
  return merged;
}

// One batched query for a whole set of uploads: upload_id -> ExtractionView
// (a deleted id is simply absent from the map). Polls as ONE request while any
// upload is still extracting, mirroring the per-item hook's cadence.
export const useUploadsExtracted = (uploadIds: string[]) => {
  const http = useAgentsHttp();
  const signature = idsSignature(uploadIds);
  return useQuery<Record<string, ExtractionView>>(
    queryKeys.uploadsExtractedBatch(signature),
    () => fetchBatch(http, uploadIds),
    {
      enabled: uploadIds.length > 0,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60_000,
      refetchInterval: (views) =>
        views &&
        Object.values(views).some((v) => isExtractionInFlight(v.extraction_status))
          ? POLL_MS
          : false,
    },
  );
};
